import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useUnreadCount from '../../hooks/useUnreadCount';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import ConfidentialWatermark from '../ConfidentialWatermark';
import { Shield, ArrowLeft, Sun, Moon, LogOut, CalendarRange, Store, ClipboardList, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const OPS_TABS = [
  { label: 'Event Calendar', path: '/employee/ops/events', icon: CalendarRange },
  { label: 'Events Manager', path: '/employee/ops/events-manager', icon: ClipboardList },
  { label: 'Retail Calendar', path: '/employee/ops/retail', icon: Store },
  { label: 'Staffing Requests', path: '/employee/ops/staffing', icon: ClipboardList },
  { label: 'Messages', path: '/employee/ops/messages', icon: ClipboardList },
  { label: 'My Deletion Requests', path: '/employee/ops/deletion-requests', icon: Trash2 },
];

// Full-width shell for the OPERATIONS pages inside the employee portal.
// The employee's MY AREA stays untouched — this is an additional area for
// staff with admin_department === 'Operations'.
export default function OpsLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const msgUnread = useUnreadCount(`${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-messages/unread-count`);
  const { theme, toggleTheme } = useTheme();

  if (user && !(user.role === 'employee' && user.admin_department === 'Operations')) {
    return <Navigate to="/employee" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ConfidentialWatermark user={user} />
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/employee')}
              data-testid="ops-back-to-my-area"
              className="text-muted-foreground hover:text-foreground -ml-2 shrink-0"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> My Area
            </Button>
            <Shield className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
            <span className="font-barlow tracking-wider text-primary uppercase text-sm sm:text-base truncate">
              San Security — <span className="text-blue-400">Operations</span>
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="ops-theme-toggle" className="text-muted-foreground hover:text-foreground">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/login'); }} data-testid="ops-logout" className="text-muted-foreground hover:text-foreground">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-2 flex gap-1 overflow-x-auto pb-0.5">
          {OPS_TABS.map((t) => {
            const active = location.pathname === t.path;
            return (
              <Link
                key={t.path}
                to={t.path}
                data-testid={`ops-tab-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider whitespace-nowrap transition-colors border-b-2',
                  active ? 'bg-primary/15 text-primary border-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50'
                )}
              >
                <t.icon className={cn('w-3.5 h-3.5', !active && 'text-blue-500 dark:text-blue-400')} strokeWidth={1.75} />
                {t.label}
                {t.path.endsWith('/messages') && msgUnread > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="dept-messages-unread-badge">{msgUnread}</span>
                )}
              </Link>
            );
          })}
        </div>
      </header>
      <main className="flex-1 pt-28 pb-8 px-4">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
